const mem = new Map<string, { url: string; isObject: boolean; ref: number }>();

async function fromCacheOrFetch(src: string): Promise<Response> {
  try {
    const cacheStorage: CacheStorage | undefined = (globalThis as any).caches;
    if (!cacheStorage) throw new Error('no caches');
    const cache = await cacheStorage.open('atlas-image-cache-v1');
    const req = new Request(src, { mode: 'cors' });
    const match = await cache.match(req);
    if (match) return match;
    const res = await fetch(req);
    if (res && res.ok) {
      try { await cache.put(req, res.clone()); } catch {}
    }
    return res;
  } catch {
    return fetch(src);
  }
}

export async function getCachedImageUrl(src: string): Promise<{ url: string; isObject: boolean }> {
  const existing = mem.get(src);
  if (existing) {
    existing.ref += 1;
    return { url: existing.url, isObject: existing.isObject };
  }
  try {
    const res = await fromCacheOrFetch(src);
    if (!res || !res.ok) throw new Error('fetch failed');
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    mem.set(src, { url, isObject: true, ref: 1 });
    return { url, isObject: true };
  } catch {
    mem.set(src, { url: src, isObject: false, ref: 1 });
    return { url: src, isObject: false };
  }
}

export function releaseCachedImageUrl(src: string) {
  const entry = mem.get(src);
  if (!entry) return;
  entry.ref -= 1;
  if (entry.ref <= 0) {
    if (entry.isObject) {
      try { URL.revokeObjectURL(entry.url); } catch {}
    }
    mem.delete(src);
  }
}

// Cache by a stable key (e.g., bucket path) while fetching from a signed/volatile URL.
export async function getCachedImageByKey(key: string, fetchUrl: string): Promise<{ url: string; isObject: boolean }> {
  const existing = mem.get(key);
  if (existing) {
    existing.ref += 1;
    return { url: existing.url, isObject: existing.isObject };
  }
  const cacheKeyUrl = `https://atlas-cache.local/${encodeURIComponent(key)}`;
  try {
    const cacheStorage: CacheStorage | undefined = (globalThis as any).caches;
    if (!cacheStorage) throw new Error('no caches');
    const cache = await cacheStorage.open('atlas-image-cache-v1');
    const req = new Request(cacheKeyUrl, { mode: 'cors' });
    const match = await cache.match(req);
    if (match && match.ok) {
      const blob = await match.blob();
      const url = URL.createObjectURL(blob);
      mem.set(key, { url, isObject: true, ref: 1 });
      return { url, isObject: true };
    }
    // Fetch from the actual URL and store under the stable key
    const res = await fetch(new Request(fetchUrl, { mode: 'cors' }));
    if (!res.ok) throw new Error('fetch failed');
    try { await cache.put(req, res.clone()); } catch {}
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    mem.set(key, { url, isObject: true, ref: 1 });
    return { url, isObject: true };
  } catch {
    mem.set(key, { url: fetchUrl, isObject: false, ref: 1 });
    return { url: fetchUrl, isObject: false };
  }
}

export function releaseCachedByKey(key: string) {
  const entry = mem.get(key);
  if (!entry) return;
  entry.ref -= 1;
  if (entry.ref <= 0) {
    if (entry.isObject) {
      try { URL.revokeObjectURL(entry.url); } catch {}
    }
    mem.delete(key);
  }
}
